import { forwardRef, HTMLAttributes, useRef, useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { TagChip } from '../TagChip/TagChip';

export interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

export interface FilterBarProps extends HTMLAttributes<HTMLDivElement> {
  options: FilterOption[];
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
  multiSelect?: boolean;
  showAll?: boolean;
  allLabel?: string;
  scrollable?: boolean;
}

export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(
  (
    {
      options,
      selected,
      onSelectionChange,
      multiSelect = false,
      showAll = true,
      allLabel = '전체',
      scrollable = true,
      className,
      ...props
    },
    ref
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);

    // Check scroll position for fade indicators
    useEffect(() => {
      const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;

        setShowLeftFade(el.scrollLeft > 0);
        setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
      };

      const el = scrollRef.current;
      if (el) {
        checkScroll();
        el.addEventListener('scroll', checkScroll, { passive: true });
        window.addEventListener('resize', checkScroll);
      }

      return () => {
        el?.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }, [options]);

    const handleTagClick = (id: string) => {
      if (multiSelect) {
        // Toggle selection
        if (selected.includes(id)) {
          onSelectionChange(selected.filter((s) => s !== id));
        } else {
          onSelectionChange([...selected, id]);
        }
      } else {
        // Single select
        onSelectionChange(selected.includes(id) ? [] : [id]);
      }
    };

    const handleAllClick = () => {
      onSelectionChange([]);
    };

    const isAllSelected = selected.length === 0;

    return (
      <div
        ref={ref}
        className={clsx('relative', className)}
        {...props}
      >
        {/* Scroll fade indicators */}
        <AnimatePresence>
          {scrollable && showLeftFade && (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none"
            />
          )}
          {scrollable && showRightFade && (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none"
            />
          )}
        </AnimatePresence>

        {/* Tags container */}
        <div
          ref={scrollRef}
          className={clsx(
            'flex gap-2 py-2',
            scrollable && 'overflow-x-auto scrollbar-hide'
          )}
        >
          {showAll && (
            <TagChip
              label={allLabel}
              selected={isAllSelected}
              onClick={handleAllClick}
            />
          )}

          {options.map((option) => (
            <TagChip
              key={option.id}
              label={option.label}
              count={option.count}
              selected={selected.includes(option.id)}
              onClick={() => handleTagClick(option.id)}
            />
          ))}
        </div>
      </div>
    );
  }
);

FilterBar.displayName = 'FilterBar';
